import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getL1SheetProperty,
  getL2SheetProperty,
} from "../../../features/reduxworkbook/WorkbookSlice";
import { formatNumber } from "../Utils/FormatNumbers";
import { useGetDataMutation } from "../../../features/reduxworkbook/dataApiSlice";
import { calculateNumber } from "./calculateNumber";

const getWinnerName = ({ data, rank, nameKey, valueKeys }) => {
  if (!data || !rank || !nameKey.length) {
    return "N/A ";
  }
  const nameResponseKey = nameKey[0].responseKey;
  const requiredIndex = rank - 1;
  if (data.length - 1 < requiredIndex) {
    return "N/A ";
  }
  const requiredRow = data[requiredIndex];

  return requiredRow[nameResponseKey];
};

const RankTileComp = ({
  Data,
  valueKeys,
  Sheetid,
  groupByKeys: rawGroupByKeys,
  isSheetInDashboard,
}) => {
  // console.log(isSheetInDashboard);

  const [getData, { data: totalData, isLoading, isSuccess, isError, error }] =
    useGetDataMutation();

  const rank = useSelector((state) =>
    getL2SheetProperty(state, Sheetid, "query", "rowLimit")
  );

  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );

  let {
    imageUrl,
    numberFontColor,
    numberFontSize,
    isIconVisible,
    isNumberVisible,
    isLabelVisible,
    labelFontColor,
    labelFontSize,
    imageWidth: imageSize,
    imageHeight,
    label,
    labelAlignment,
    isRanknameVisible,
    rankFontColor,
    rankFontSize = 10,
    dashboardBoxSize,
    rankAlignment,
    isLabelBold,
    isNumberBold,
    isRankBold,
  } = customizationObject;

  const winnerName = getWinnerName({
    data: Data,
    rank,
    nameKey: rawGroupByKeys,
    valueKeys,
  });
  // console.log(imageUrl);

  const number = useMemo(
    () =>
      calculateNumber(
        Data,
        valueKeys,
        rawGroupByKeys,

        totalData,
        isSuccess
      ),
    [Data, valueKeys, rawGroupByKeys, totalData, isSuccess]
  );

  const numberObject = valueKeys[0]?.numberFormat;
  function setOrder(labelAlignment, rankAlignment) {
    let order = { numberOrder: 2, labelOrder: 3, rankOrder: 1 };
    if (labelAlignment && rankAlignment) {
      if (labelAlignment.value === rankAlignment.value) {
        order = { numberOrder: 1, labelOrder: 2, rankOrder: 3 };
      } else {
        if (
          labelAlignment.value === "top" &&
          rankAlignment.value === "bottom"
        ) {
          order = { numberOrder: 2, labelOrder: 1, rankOrder: 3 };
        }
        if (
          labelAlignment.value === "top" &&
          rankAlignment.value === "center"
        ) {
          order = { numberOrder: 3, labelOrder: 1, rankOrder: 2 };
        }
        if (
          labelAlignment.value === "center" &&
          rankAlignment.value === "bottom"
        ) {
          order = { numberOrder: 1, labelOrder: 2, rankOrder: 3 };
        }
        if (
          rankAlignment.value === "top" &&
          labelAlignment.value === "bottom"
        ) {
          order = { numberOrder: 2, labelOrder: 3, rankOrder: 1 };
        }
        if (
          rankAlignment.value === "top" &&
          labelAlignment.value === "center"
        ) {
          order = { numberOrder: 3, labelOrder: 2, rankOrder: 1 };
        }
        if (
          rankAlignment.value === "center" &&
          labelAlignment.value === "bottom"
        ) {
          order = { numberOrder: 1, labelOrder: 3, rankOrder: 2 };
        }
      }
    } else {
      order = { numberOrder: 2, labelOrder: 3, rankOrder: 1 };
    }
    return order;
  }
  const orders = setOrder(labelAlignment, rankAlignment);
  // console.log(orders);
  const { numberOrder, labelOrder, rankOrder } = orders;
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const updateSize = () => {
      if (isSheetInDashboard && dashboardBoxSize) {
        setDimensions({
          width: dashboardBoxSize.width,
          height: dashboardBoxSize.height,
        });
      } else {
        if (containerRef.current) {
          setDimensions({
            width: containerRef.current.offsetWidth,
            height: containerRef.current.offsetHeight,
          });
        }
      }
    };

    updateSize(); // Initial update
    window.addEventListener("resize", updateSize); // Update on window resize

    return () => window.removeEventListener("resize", updateSize);
  }, [dashboardBoxSize]);

  const imageWidth = (dimensions.width * imageSize) / 80;
  imageHeight = dimensions.height;

  labelFontSize = isSheetInDashboard
    ? (dimensions.width * labelFontSize) / 1100
    : (dimensions.width * labelFontSize) / 1000;
  numberFontSize = isSheetInDashboard
    ? (dimensions.width * numberFontSize) / 1100
    : (dimensions.width * numberFontSize) / 1000;
  rankFontSize = isSheetInDashboard
    ? (dimensions.width * rankFontSize) / 1100
    : (dimensions.width * rankFontSize) / 1000;

  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center bg-white w-full h-full"
    >
      <div className="w-full h-full flex items-center justify-start gap-x-2">
        {/* Image Section */}
        {isIconVisible && imageUrl ? (
          <div className="flex flex-col items-center justify-center h-full">
            <img
              src={imageUrl}
              alt="Image"
              className="object-contain"
              style={{ width: imageWidth, height: imageHeight }}
            />
          </div>
        ) : null}
        <div className="w-auto flex flex-col gap-0 items-start justify-center overflow-hidden truncate h-full">
          {/* Rank Name */}
          {isRanknameVisible && (
            <div
              className={`w-auto  ${isRankBold ? "font-bold" : ""}`}
              style={{
                fontSize: rankFontSize,
                color: rankFontColor ?? "#000000",
                order: rankOrder,
              }}
            >
              {winnerName}
            </div>
          )}

          {/* Number */}
          {isNumberVisible && number && (
            <div
              className={`w-auto  ${isNumberBold ? "font-bold" : ""}`}
              style={{
                fontSize: numberFontSize,
                color: numberFontColor ?? "#000000",
                order: numberOrder,
              }}
            >
              {formatNumber(number, numberObject)}
            </div>
          )}

          {/* Label */}
          {isLabelVisible && (
            <div
              className={`w-auto ${isLabelBold ? "font-bold" : ""}`}
              style={{
                fontSize: labelFontSize,
                color: labelFontColor ?? "#000000",
                order: labelOrder,
              }}
            >
              {label || "your text here..."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RankTileComp;
